import React from "react"
import SubsectionContainer from "../shared/SubsectionContainer"
import {
  SubsectionContent,
  SubsectionContentList,
} from "../shared/SubsectionContent"
import {
  ProviderCredential,
  ProviderLicense,
} from "../../../domain/Provider/schema"
import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import { useLazyCmsCollectionMap } from "../../../domain/CMS/useLazyCmsCollectionMap"
import { getLicenseStatus, isLicenseStatusWarning } from "../utils/licenseUtils"
import { Badge } from "../../shared/components/Badge"
import Typography, { typographyTypes } from "../../shared/components/Typography"

interface QualificationsContentProps {
  credentials: ProviderCredential[]
  licenses: ProviderLicense[]
  graduateDegreeDisplayId: string
  graduateSchoolName: string
  graduateYear: string
  isUnderSupervision: boolean
}

export const QualificationsContent: React.FC<QualificationsContentProps> = ({
  credentials,
  licenses,
  graduateDegreeDisplayId,
  graduateSchoolName,
  graduateYear,
  isUnderSupervision,
}) => {
  const { cms: cmsContent } = useCMSReady([
    "common",
    "publicProfileSlide",
    "providerQualificationsCredentialTypeMap",
    "providerQualificationsDegreeTypeMap",
  ])
  const commonText = cmsContent.common
  const publicProfileContent = cmsContent.publicProfileSlide

  const credentialMap = useLazyCmsCollectionMap(
    "credentialTypes",
    "providerQualificationsCredentialTypeMap",
  )
  const degreeMap = useLazyCmsCollectionMap(
    "graduateDegrees",
    "providerQualificationsDegreeTypeMap",
  )

  const providerLicenseTypeOptions = useLazyCmsCollectionMap(
    "licenseTypes",
    "providerQualificationsLicenseTypeMap",
  )

  const licenseList = licenses.map((license) => {
    return (
      <div key={license.id}>
        <div>
          {`${publicProfileContent?.licensingSubsectionLicensedBy ?? ""} ${license?.stateCode ?? ""}`}
        </div>
        <div>
          {`${publicProfileContent?.licensingSubsectionLicenseNumber ?? ""} ${license?.licenseNumber ?? ""}`}
        </div>
        <div>{`${publicProfileContent?.licenseToolTipLicenseTypeText ?? ""} ${providerLicenseTypeOptions[license.licenseTypeDisplayId!] ?? ""}`}</div>
        <div
          className={
            isLicenseStatusWarning(license)
              ? "font-bold !text-aaa-error-red"
              : ""
          }
        >
          {getLicenseStatus(license, publicProfileContent)}
        </div>
      </div>
    )
  })

  const credentialList = credentials.map((credential) => (
    <div key={credential.id}>
      <div>
        {`${publicProfileContent?.credentialsSubsectionCredentialedBy} ${credential.stateCode ?? ""}`}
      </div>
      <div>
        {`${publicProfileContent?.credentialsSubsectionCredential} ${credentialMap[credential.credentialTypeDisplayId]} `}
      </div>
    </div>
  ))

  const formatGraduateInfo = (schoolName?: string, year?: string) => {
    if (schoolName && year) return `${schoolName}, ${year}`
    return schoolName || year || ""
  }

  const graduateInfo = formatGraduateInfo(graduateSchoolName, graduateYear)

  const educationContent = (
    <>
      <Typography
        text={degreeMap[graduateDegreeDisplayId]}
        variant={typographyTypes.NORMAL}
      />
      {graduateInfo && (
        <Typography text={graduateInfo} variant={typographyTypes.NORMAL} />
      )}
    </>
  )

  return (
    <SubsectionContainer>
      {licenses?.length > 0 && (
        <SubsectionContent
          title={publicProfileContent?.licensingSubsectionTitle}
        >
          {isUnderSupervision && (
            <div className="flex flex-row items-center gap-2 pb-2">
              <Badge
                text={commonText?.underSupervision ?? ""}
                key="1"
                color="info"
                styleOverrides="px-2.5"
              />
            </div>
          )}
          <SubsectionContentList
            listItems={licenseList}
            listItemsStyles="flex flex-col gap-5"
          />
        </SubsectionContent>
      )}
      {credentials?.length > 0 && (
        <SubsectionContent
          title={publicProfileContent?.credentialsSubsectionTitle}
        >
          <SubsectionContentList
            listItems={credentialList}
            listItemsStyles="flex flex-col gap-5"
          />
        </SubsectionContent>
      )}
      {!!(graduateDegreeDisplayId || graduateYear || graduateSchoolName) && (
        <SubsectionContent
          title={publicProfileContent?.educationSubsectionTitle}
        >
          {educationContent}
        </SubsectionContent>
      )}
    </SubsectionContainer>
  )
}
